import { Building2 } from 'lucide-react';
import { COMPANY_SUB_LABELS, CompanySubKind, FillVariable } from '../utils';
import { RenderTextForm } from './render-text-form';
import { CompanyPicker } from '@/modules/org/companies/components/CompanyPicker';
import type { Company } from '@/modules/org/companies/types/companies.types';

interface Props {
  parent: FillVariable;
  fieldErrors: Set<string>;
  childrenByParent: Map<string, FillVariable[]>;
  values: Record<string, string>;
  setValue: (name: string, value: string) => void;
  setCompanyValue: (name: string, value: string) => void;
  applyCompanyValues: (
    parent: FillVariable,
    src: Partial<Record<CompanySubKind, string | null | undefined>>
  ) => void;
}

/** Map a saved Company record onto the sub-kinds a Company entity fills. */
export const companyToSubKinds = (
  company: Company
): Partial<Record<CompanySubKind, string | null | undefined>> => ({
  name: company.name,
  address: company.address,
  website: company.website,
  email: company.email,
  phone: company.phone,
});

/**
 * A Company entity's card in the fill form — the company counterpart of
 * `RenderPersonForm`. "Use a company" fills every marked sub-field in one action, the
 * same way "Use a contact" fills a person's.
 */
export default function RenderCompanyForm({
  parent,
  fieldErrors,
  childrenByParent,
  values,
  setValue,
  setCompanyValue,
  applyCompanyValues,
}: Props) {
  const kids = childrenByParent.get(parent.id) ?? [];

  // Seed the picker's "create new company" form with whatever is already typed here.
  const bySub: Partial<Record<CompanySubKind, string>> = {};
  for (const c of kids) if (c.companySubKind) bySub[c.companySubKind] = values[c.name] ?? '';

  return (
    <div className="space-y-4 rounded-xl border border-border bg-[hsl(var(--muted))]/20 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-selise-blue" />
          <span className="text-sm font-semibold text-[hsl(var(--fg))]">{parent.label}</span>
        </div>

        <CompanyPicker
          label="Use a company"
          onSelect={(co) => applyCompanyValues(parent, companyToSubKinds(co))}
          createDefaults={{
            name: bySub.name ?? '',
            address: bySub.address ?? '',
            website: bySub.website ?? '',
            email: bySub.email ?? '',
            phone: bySub.phone ?? '',
          }}
        />
      </div>

      <div className="space-y-4">
        {kids.map((child) => (
          <div key={child.name}>
            <RenderTextForm
              invalid={fieldErrors.has(child.name)}
              value={values[child.name]}
              setCompanyValue={setCompanyValue}
              setValue={setValue}
              v={child}
              opts={{
                subLabel: `${parent.label} — ${
                  COMPANY_SUB_LABELS[child.companySubKind ?? 'name']
                }`,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
